/**
 * InboxWaitingNotifier — global agent popup for waiting/auto-assigned chats.
 *
 * Mounted once in the authenticated route tree (alongside AIChatOverlay) so
 * inbox agents/managers get the "customer is waiting" claim popup and the
 * "chat assigned" toast on ANY page, not only inside the inbox.
 *
 * - Subscribes to the inbox SSE stream (only when the user can attend chats).
 * - Plays a short notification ping on new events (WebAudio — no asset file).
 * - Claim → POST attend, then navigate to the inbox.
 */
import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../provider/AuthProvider';
import api from '../api/api';
import { showNotification } from '../pages/components/Notification';
import useInboxWaitingStream from '../hooks/useInboxWaitingStream';

// Short two-tone ping via the Web Audio API — avoids shipping/serving an asset
// and the 404/path risks that come with it. Guarded: autoplay policy may block
// audio until the user has interacted with the page (agents always have).
const playPing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const beep = (freq, start, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.value = freq;
      const t = ctx.currentTime + start;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.start(t);
      o.stop(t + dur + 0.02);
    };
    beep(880, 0, 0.18);
    beep(1175, 0.16, 0.22);
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 700);
  } catch { /* noop */ }
};

const InboxWaitingNotifier = () => {
  const { userdetails } = useAuth();
  const navigate = useNavigate();
  const [waitingPopups, setWaitingPopups] = useState([]);

  const groups = userdetails?.group_names || [];
  const isManager = Boolean(
    userdetails?.is_superuser
    || groups.includes('Administration / Settings')
    || groups.includes('Inbox / Manager')
  );
  const isAgent = groups.includes('Inbox / Agent') && !isManager;
  const canAttend = isAgent || isManager;

  const dismiss = useCallback((conversationId) => {
    setWaitingPopups((prev) => prev.filter((p) => p.conversation_id !== conversationId));
  }, []);

  const claim = useCallback(async (conversationId) => {
    dismiss(conversationId);
    try {
      await api.post(`/inbox/conversations/${conversationId}/attend/`);
      showNotification('Chat attended successfully', 'success');
      navigate('/inbox/chats');
    } catch (error) {
      if (error.response?.status === 409) {
        showNotification('Chat already attended by another agent', 'error');
      } else {
        showNotification(
          error.response?.data?.message || 'Failed to attend chat',
          'error'
        );
      }
    }
  }, [dismiss, navigate]);

  useInboxWaitingStream(canAttend, {
    onWaitingChat: (data) => {
      setWaitingPopups((prev) =>
        prev.some((p) => p.conversation_id === data.conversation_id)
          ? prev
          : [...prev, data]
      );
      playPing();
    },
    // One stream per tab: events for other listeners (the Voice Agent Desk phone) are re-broadcast on window.
    onOtherEvent: (data) => {
      window.dispatchEvent(new CustomEvent('xenbox:agent-event', { detail: data }));
    },
    onConnected: () => {
      window.dispatchEvent(new CustomEvent('xenbox:agent-event', { detail: { type: 'connected' } }));
    },
    onChatAssigned: (data) => {
      showNotification(
        `New chat assigned: ${data.contact_name || 'customer'}`,
        'success'
      );
      playPing();
    },
  });

  if (!canAttend || waitingPopups.length === 0) return null;

  const p = waitingPopups[waitingPopups.length - 1];
  const waitedMin =
    typeof p.waiting_delayed_seconds === 'number' && p.waiting_delayed_seconds >= 60
      ? Math.floor(p.waiting_delayed_seconds / 60)
      : 0;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={() => dismiss(p.conversation_id)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--cx-surface)', borderRadius: 18, boxShadow: '0 24px 70px rgba(0,0,0,0.35)', padding: 36, width: 'min(92vw, 480px)', textAlign: 'center' }}
      >
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'light-dark(#dbeafe, #142743)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', fontSize: 34 }}>
          💬
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 6, color: 'var(--cx-ink)' }}>
          A customer is waiting
        </div>
        <div style={{ fontSize: 16, color: 'var(--cx-text-2)', marginBottom: 4 }}>
          {(p.contact_name || 'Customer')} · {(p.channel || '').toUpperCase()}
        </div>
        {waitedMin > 0 && (
          <div style={{ fontSize: 14, color: '#dc2626', fontWeight: 600, marginBottom: 4 }}>
            Waiting {waitedMin} min
          </div>
        )}
        {waitingPopups.length > 1 && (
          <div style={{ fontSize: 13, color: 'var(--cx-text-3)', marginBottom: 4 }}>
            +{waitingPopups.length - 1} more waiting
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            onClick={() => dismiss(p.conversation_id)}
            style={{ flex: 1, background: 'var(--cx-canvas-3)', color: 'var(--cx-text-2)', border: 'none', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', fontSize: 15, fontWeight: 600 }}
          >
            Dismiss
          </button>
          <button
            onClick={() => claim(p.conversation_id)}
            style={{ flex: 2, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', fontSize: 16, fontWeight: 700 }}
          >
            Attend now
          </button>
        </div>
      </div>
    </div>
  );
};

export default InboxWaitingNotifier;
